import { Modal } from '@WebComponents/Modal';
import { yupResolver } from '@hookform/resolvers/yup';
import { css } from 'aphrodite';
import { useCallback, useMemo, useState } from 'react';
import { useForm } from 'react-hook-form';
import { useHistory } from 'react-router';
import { useStripePublishableKeyByToken } from '../../../../queries';
import { useClientCalculateDonationFee } from '../../../../queries/Surveys/useClientCalculateDonationFee';
import { useClientGetStripeCheckoutSession } from '../../../../queries/Surveys/useClientGetStripeCheckoutSession';
import { useClientStripeSessionByToken } from '../../../../queries/Surveys/useClientStripeSessionByToken';
import { DonationFrequency, IDonationSurvey, IDonationSurveyResponse } from '../../../models/Api';
import { migrateLegacyDonationOptions } from '../../../models/DonationSurveyMigration';
import { CheckoutFormProviderWrap } from '../../StripeCheckout';
import { DonationForm } from './DonationForm';
import { ThankYouMessage } from './ThankYou';
import { styleSheet } from './styles';
import { IFormValues, donationSurveyResponseValidationSchema } from './utils';

interface IProps {
	survey: IDonationSurvey;
	token: string;
}

const formatCentsAsDollars = (cents: number): string => (cents / 100).toFixed(2);

interface DonationCheckoutContentProps {
	donationResponse: IDonationSurveyResponse;
	stripePublicId: string;
	token: string;
	onSuccessClose: (sessionId: string) => void;
	onCloseModal: (sessionId: string) => void;
}

export const DonationCheckoutContent = ({
	donationResponse,
	stripePublicId,
	token,
	onSuccessClose,
	onCloseModal,
}: DonationCheckoutContentProps) => {
	const stripeSession = useClientStripeSessionByToken();
	const clientSecretPromise = useMemo(() => {
		return stripeSession.mutateAsync({
			token,
			donationAmount: donationResponse.donationAmount,
			serviceFeeAmount: donationResponse.serviceFee,
			donorCoversFees: donationResponse.donorCoversFees,
			response: donationResponse,
			isRecurring: donationResponse.donationFrequency !== null && donationResponse.donationFrequency !== undefined,
			donationFrequency: donationResponse.donationFrequency,
			_type: 'DonationSurveyCheckout',
		});
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, []);

	const defaultValues = useMemo(
		() => ({
			billingAddress: {
				name: donationResponse?.donor
					? `${donationResponse.donor.firstName} ${donationResponse.donor.lastName}`
					: undefined,
				address: {
					line1: donationResponse?.donor?.address?.address1,
					line2: donationResponse?.donor?.address?.address2,
					city: donationResponse?.donor?.address?.city,
					state: donationResponse?.donor?.address?.stateProvince,
					postal_code: donationResponse?.donor?.address?.postalCode,
					country: donationResponse?.donor?.address?.country || 'US',
				},
			},
		}),
		[donationResponse]
	);

	const totalAmount = donationResponse.donorCoversFees
		? donationResponse.donationAmount + donationResponse.serviceFee
		: donationResponse.donationAmount;

	const orderSummary = (
		<>
			<p>
				Donation Amount: <strong>${formatCentsAsDollars(donationResponse.donationAmount)}</strong>
			</p>
			{donationResponse.donorCoversFees && (
				<p>
					Service Fee: <strong>${formatCentsAsDollars(donationResponse.serviceFee)}</strong>
				</p>
			)}
			<p>
				Total: <strong>${formatCentsAsDollars(totalAmount)}</strong>
			</p>
		</>
	);

	return (
		<CheckoutFormProviderWrap
			stripePublicId={stripePublicId}
			clientSecretPromise={clientSecretPromise}
			defaultValues={defaultValues}
			prefilledEmail={donationResponse?.donor?.email ?? ''}
			onSuccessClose={onSuccessClose}
			onCloseModal={onCloseModal}
			sectionTitle='Your Donation'
			orderSummary={orderSummary}
			submitButtonLabel={`Donate $${formatCentsAsDollars(totalAmount)}`}
			loggingName='DonationSurveyCheckoutForm'
		/>
	);
};

export const DonationSurvey = ({ survey, token }: IProps) => {
	const history = useHistory<{ search?: { checkoutStarted?: boolean } }>();
	const [showCheckoutForm, setShowCheckoutForm] = useState<boolean>(false);
	const [donationResponse, setDonationResponse] = useState<IDonationSurveyResponse | null>(null);
	const [stripeSessionId, setStripeSessionId] = useState<string | null>(null);
	const { data: checkoutSession, isLoading: isCheckoutSessionLoading } = useClientGetStripeCheckoutSession({
		sessionId: stripeSessionId,
		refetchOnWindowFocus: false,
		token,
	});

	const { data: stripePublicId } = useStripePublishableKeyByToken({
		accessToken: token,
		refetchOnWindowFocus: false,
	});

	// Migrate legacy donation options to new frequency-specific structure
	const migratedSurvey = useMemo(() => {
		return {
			...survey,
			donationOptions: migrateLegacyDonationOptions(
				survey.donationOptions,
				survey.donationOptions?.allowRecurringDonations || false
			),
		};
	}, [survey]);

	// Get the one-time donation settings for default values
	const oneTimeSettings = migratedSurvey.donationOptions.oneTimeDonationSettings;

	const defaultValues: IFormValues = useMemo(() => {
		return {
			amount:
				oneTimeSettings?.enableSuggestedAmounts && oneTimeSettings.suggestedAmounts[0]
					? oneTimeSettings.suggestedAmounts[0]
					: null,
			customAmount: null as number | null,
			campaignId: '',
			coverFees: false,
			donationFrequency: null as DonationFrequency | null,
			address: {
				address1: '',
				address2: '',
				city: '',
				stateProvince: '',
				postalCode: '',
				country: '',
			},
			email: '',
			phone: '',
			firstName: '',
			lastName: '',
			customProperties: {},
		};
	}, [oneTimeSettings]);

	const validationSchema = useMemo(() => {
		return donationSurveyResponseValidationSchema(survey);
	}, [survey]);

	const { setValue, watch, handleSubmit, control, formState } = useForm({
		defaultValues,
		resolver: yupResolver(validationSchema),
	});

	const formValues = watch();

	const { data: fees, isLoading: isLoadingFees } = useClientCalculateDonationFee({
		surveyId: survey.id,
		value: formValues.customAmount ? formValues.customAmount * 100 : formValues.amount * 100,
	});

	const onSubmit = useCallback(
		async (data: IFormValues) => {
			if (!fees) {
				return;
			}

			const response: IDonationSurveyResponse = {
				donationAmount: fees.donationAmount,
				serviceFee: data.coverFees ? fees.serviceFee : 0,
				totalCharge: data.coverFees ? fees.totalCharge : fees.donationAmount,
				donorCoversFees: data.coverFees,
				selectedCampaignId: data.campaignId,
				confirmationNumber: '',
				donor: {
					firstName: data.firstName || '',
					lastName: data.lastName || '',
					email: data.email || '',
					phone: data.phone || '',
					address: {
						city: data.address.city,
						country: data.address.country,
						postalCode: data.address.postalCode,
						stateProvince: data.address.stateProvince,
						address1: data.address.address1,
						address2: data.address.address2,
					},
				},
				customProperties: data.customProperties,
				donationFrequency: data.donationFrequency,
				isMirrored: false,
				token,
				surveyId: survey.id,
			};

			setDonationResponse(response);
			setShowCheckoutForm(true);
		},
		[fees, survey.id, token]
	);

	const handleCloseCheckoutForm = useCallback(() => {
		setShowCheckoutForm(false);
	}, []);

	const onSuccessCloseCheckoutForm = useCallback(
		(sessionId: string) => {
			setShowCheckoutForm(false);

			setStripeSessionId(sessionId);

			history.replace({
				pathname: `/donation/${token}`,
				search: '',
			});
		},
		[history, token]
	);

	if (checkoutSession) {
		return <ThankYouMessage />;
	}

	return (
		<div className={css(styleSheet.container)}>
			<DonationForm
				survey={migratedSurvey}
				formValues={formValues}
				setValue={setValue}
				onSubmit={handleSubmit(onSubmit)}
				control={control}
				formState={formState}
				isLoadingFees={isLoadingFees}
				isLoadingCheckoutSession={isCheckoutSessionLoading}
			/>
			<Modal
				isOpen={showCheckoutForm}
				onRequestClose={handleCloseCheckoutForm}
				useDefaultHeader={false}
				shouldCloseOnOverlayClick={false}
			>
				{donationResponse && stripePublicId ? (
					<DonationCheckoutContent
						donationResponse={donationResponse}
						stripePublicId={stripePublicId}
						token={token}
						onSuccessClose={onSuccessCloseCheckoutForm}
						onCloseModal={() => handleCloseCheckoutForm()}
					/>
				) : null}
			</Modal>
		</div>
	);
};
